import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Award, CheckCircle, XCircle, Clock, User, BookOpen, Percent, Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminScholarships = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scholarships/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const processApplication = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/scholarships/applications/${id}/process?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Application ${status}`);
      fetchApplications();
    } catch (error) { toast.error('Failed to process'); }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { className: 'bg-yellow-100 text-yellow-700', icon: Clock },
      approved: { className: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { className: 'bg-red-100 text-red-700', icon: XCircle }
    };
    return config[status] || { className: '', icon: Clock };
  };

  const filteredApplications = filterStatus === 'all'
    ? applications
    : applications.filter(a => a.status === filterStatus);

  return (
    <AdminLayout title="Scholarship Applications">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className={`cursor-pointer transition-shadow ${filterStatus === 'pending' ? 'ring-2 ring-yellow-400' : ''}`} onClick={() => setFilterStatus(filterStatus === 'pending' ? 'all' : 'pending')}>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{applications.filter(a => a.status === 'pending').length}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-shadow ${filterStatus === 'approved' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setFilterStatus(filterStatus === 'approved' ? 'all' : 'approved')}>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{applications.filter(a => a.status === 'approved').length}</p>
            <p className="text-sm text-muted-foreground">Approved</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-shadow ${filterStatus === 'rejected' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setFilterStatus(filterStatus === 'rejected' ? 'all' : 'rejected')}>
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
            <p className="text-2xl font-bold">{applications.filter(a => a.status === 'rejected').length}</p>
            <p className="text-sm text-muted-foreground">Rejected</p>
          </CardContent>
        </Card>
      </div>

      {filterStatus !== 'all' && (
        <div className="mb-4 flex items-center gap-2">
          <Badge variant="outline">Filtering: {filterStatus}</Badge>
          <Button variant="ghost" size="sm" onClick={() => setFilterStatus('all')}>Clear filter</Button>
        </div>
      )}

      {/* Applications List */}
      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-24"></CardContent></Card>)}</div>
      ) : filteredApplications.length > 0 ? (
        <div className="space-y-4">
          {filteredApplications.map((app) => {
            const statusConfig = getStatusBadge(app.status);
            const StatusIcon = statusConfig.icon;
            return (
              <Card key={app.id} data-testid={`scholarship-app-${app.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Student Info */}
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-semibold">
                          {app.student?.name || `Student ${app.student_id?.slice(-8)}`}
                        </h4>
                        <Badge className={statusConfig.className}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {app.status}
                        </Badge>
                      </div>

                      {/* Contact details */}
                      {app.student && (
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                          {app.student.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" /> {app.student.email}
                            </span>
                          )}
                          {app.student.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" /> {app.student.phone}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Scholarship & Course Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg mb-2">
                        <div className="flex items-start gap-2">
                          <Percent className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">
                              {app.scholarship?.name || `Scholarship ${app.scholarship_id?.slice(-8)}`}
                            </p>
                            {app.scholarship?.discount_percentage != null && (
                              <p className="text-xs text-muted-foreground">
                                {app.scholarship.discount_percentage}% discount
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <BookOpen className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium">
                              {app.course?.name || `Course ${app.course_id?.slice(-8)}`}
                            </p>
                            <div className="flex gap-2 text-xs text-muted-foreground">
                              {app.course?.category && <span>{app.course.category}</span>}
                              {app.course?.fee != null && <span>• Fee: ₹{app.course.fee.toLocaleString()}</span>}
                              {app.scholarship?.discount_percentage != null && app.course?.fee != null && (
                                <span>• After discount: ₹{(app.course.fee * (1 - app.scholarship.discount_percentage / 100)).toLocaleString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Remarks & Date */}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span>Applied: {app.applied_at ? new Date(app.applied_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                        {app.processed_at && (
                          <span>• Processed: {new Date(app.processed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        )}
                        {app.remarks && (
                          <span className="text-foreground">• Remarks: "{app.remarks}"</span>
                        )}
                        {app.admin_remarks && (
                          <span className="text-foreground">• Admin note: "{app.admin_remarks}"</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {app.status === 'pending' && (
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button size="sm" onClick={() => processApplication(app.id, 'approved')} className="bg-green-600 hover:bg-green-700">
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => processApplication(app.id, 'rejected')}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center"><Award className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-2">No Applications</h3><p className="text-sm text-muted-foreground">{filterStatus !== 'all' ? 'No applications with this status.' : 'No scholarship applications yet.'}</p></CardContent></Card>
      )}
    </AdminLayout>
  );
};

export default AdminScholarships;
